import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  NativeDateAdapter
} from '@angular/material/core';
import { CommonDropdownComponent } from '../common-dropdown/common-dropdown.component';
import {
  DropdownInfoConfig,
  DropdownOptionLike
} from '../../constants/api-endpoints';
import { TranslatePipe } from '../../pipes/translate.pipe';

export type FilterFieldType = 'text' | 'select' | 'radio' | 'dateRange' | 'date-range' | 'switch';

export interface FilterOption {
  value: string | number;
  label: string;
}

export interface FilterField {
  key: string;
  label: string;
  type: FilterFieldType;
  placeholder?: string;
  switchLabel?: string;
  options?: FilterOption[];
  dropdownObj?: Partial<DropdownInfoConfig>;
  dependsOnKey?: string;
  dependsOnFilterKey?: string;
  dependsOnOptionsKey?: string;
  numericOnly?: boolean;
  exactMatch?: boolean;
}

class DdMmYyyyDateAdapter extends NativeDateAdapter {
  override parse(value: unknown): Date | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      const ddMmYyyy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (ddMmYyyy) {
        const day = Number(ddMmYyyy[1]);
        const month = Number(ddMmYyyy[2]);
        const year = Number(ddMmYyyy[3]);
        const parsed = new Date(year, month - 1, day);
        if (
          parsed.getFullYear() === year
          && parsed.getMonth() === month - 1
          && parsed.getDate() === day
        ) {
          return parsed;
        }
        return null;
      }
    }
    return super.parse(value);
  }

  override format(date: Date, displayFormat: unknown): string {
    if (displayFormat === 'input') {
      const day = `${date.getDate()}`.padStart(2, '0');
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
    return super.format(date, displayFormat as object);
  }
}

const DD_MM_YYYY_FORMATS = {
  parse: {
    dateInput: 'input'
  },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

export function hasMeaningfulFilter(values: Record<string, any> | null | undefined): boolean {
  if (!values) {
    return false;
  }

  return Object.keys(values).some((key) => {
    const value = values[key];
    if (key === '__showDeleted') {
      return !!value;
    }
    if (key === 'status') {
      return Number(value) === 0;
    }
    if (Array.isArray(value)) {
      return value.some((item) => `${item ?? ''}`.trim() !== '');
    }
    if (value === null || value === undefined) {
      return false;
    }
    return `${value}`.trim() !== '';
  });
}

@Component({
  selector: 'app-filter-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CommonDropdownComponent, MatDatepickerModule, MatInputModule, MatNativeDateModule, TranslatePipe],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: DateAdapter, useClass: DdMmYyyyDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMATS }
  ],
  templateUrl: './filter-modal.component.html',
  styleUrl: './filter-modal.component.css'
})
export class FilterModalComponent implements OnChanges {
  @Input() title = 'common.filter';
  @Input() fields: FilterField[] = [];
  @Input() value: Record<string, any> = {};

  @Output() apply = new EventEmitter<[Record<string, any>, boolean]>();
  @Output() reset = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  form!: FormGroup;
  private dropdownConfigMap: Record<string, Partial<DropdownInfoConfig>> = {};
  private dropdownRequestFiltersMap: Record<string, Record<string, unknown> | null> = {};
  private loadedOptionsMap: Record<string, DropdownOptionLike[]> = {};

  get orderedFields(): FilterField[] {
    const nonRadio = this.fields.filter((field) => field.type !== 'radio');
    const radio = this.fields.filter((field) => field.type === 'radio');
    return [...nonRadio, ...radio];
  }

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields']) {
      this.buildDropdownConfigs();
      this.buildForm();
    }

    if (changes['value'] && this.form) {
      this.form.patchValue(this.buildDisplayPatchValue(), { emitEvent: false });
      this.buildDropdownConfigs();
    }

    if (changes['fields'] || changes['value']) {
      this.hydrateDependentDropdowns();
    }
  }

  buildForm(): void {
    const group: Record<string, any> = {};
    this.fields.forEach((field) => {
      if (this.isDateRangeField(field)) {
        const rangeValue = this.resolveControlValue(field) as { start: Date | null; end: Date | null };
        group[`${field.key}_start`] = [rangeValue.start];
        group[`${field.key}_end`] = [rangeValue.end];
      } else {
        group[field.key] = [this.resolveControlValue(field)];
      }
    });
    this.form = this.fb.group(group);
  }

  private buildDropdownConfigs(): void {
    this.dropdownConfigMap = {};
    this.dropdownRequestFiltersMap = {};
    this.loadedOptionsMap = {};
    this.fields.forEach((field) => {
      if (field.type === 'select') {
        this.dropdownConfigMap[field.key] = this.createDropdownObj(field);
        this.dropdownRequestFiltersMap[field.key] = this.createRequestFiltersForField(field);
      }
    });
  }

  submit(): void {
    const payload: Record<string, any> = {};

    this.fields.forEach((field) => {
      const rawValue = this.form.controls[field.key]?.value;
      if (field.type === 'text') {
        const textValue = `${rawValue ?? ''}`.trim();
        const normalized = textValue.replace(/^%+|%+$/g, '').trim();
        if (!normalized) {
          payload[field.key] = '';
          return;
        }

        if (field.numericOnly || field.exactMatch) {
          payload[field.key] = normalized;
          return;
        }

        payload[field.key] = `%${normalized}%`;
        return;
      }

      if (field.type === 'switch') {
        payload[field.key] = !!rawValue;
        return;
      }

      if (this.isDateRangeField(field)) {
        const startRaw = this.form.controls[`${field.key}_start`]?.value;
        const endRaw = this.form.controls[`${field.key}_end`]?.value;
        const start = this.toApiDate(startRaw);
        const end = this.toApiDate(endRaw);
        payload[field.key] = start || end ? [start ?? '', end ?? ''] : [];
        return;
      }

      payload[field.key] = rawValue ?? '';
    });

    this.apply.emit([payload, hasMeaningfulFilter(payload)]);
  }

  resetForm(): void {
    Object.keys(this.dropdownConfigMap).forEach((key) => {
      const current = this.dropdownConfigMap[key];
      this.dropdownConfigMap[key] = {
        ...current,
        initialValue: undefined
      };
    });

    this.form.reset();
    this.reset.emit();
  }

  closeModal(): void {
    this.close.emit();
  }

  getDropdownObj(field: FilterField): Partial<DropdownInfoConfig> {
    return this.dropdownConfigMap[field.key] ?? this.createDropdownObj(field);
  }

  getDropdownRequestFilters(field: FilterField): Record<string, unknown> | null {
    return this.dropdownRequestFiltersMap[field.key] ?? this.createRequestFiltersForField(field);
  }

  private createDropdownObj(field: FilterField): Partial<DropdownInfoConfig> {
    const fallback: Partial<DropdownInfoConfig> = {
      name: field.key,
      valueKey: 'value',
      display1: 'label',
      label: field.label,
      placeholder: field.placeholder ?? `Select ${field.label}`,
      isSearchable: true
    };

    const merged = {
      ...fallback,
      ...(field.dropdownObj ?? {})
    };
    merged.initialValue = undefined;

    const hasDropdownObjOptions = !!field.dropdownObj
      && Object.prototype.hasOwnProperty.call(field.dropdownObj, 'options');
    const hasDropdownObjApiInfo = !!field.dropdownObj?.apiInfo;

    if (!hasDropdownObjOptions && !hasDropdownObjApiInfo && (field.options?.length ?? 0) > 0) {
      merged.options = field.options!.map((option) => ({
        value: option.value,
        label: option.label
      }));
    }

    return merged;
  }

  private resolveControlValue(field: FilterField): unknown {
    const inputValue = this.value?.[field.key];
    if (field.type === 'switch') {
      return !!inputValue;
    }

    if (this.isDateRangeField(field)) {
      if (Array.isArray(inputValue)) {
        return {
          start: this.toDate(inputValue[0]),
          end: this.toDate(inputValue[1])
        };
      }

      if (inputValue && typeof inputValue === 'object') {
        const obj = inputValue as Record<string, unknown>;
        return {
          start: this.toDate(obj['start']),
          end: this.toDate(obj['end'])
        };
      }

      return { start: null, end: null };
    }

    if (inputValue !== undefined && inputValue !== null && inputValue !== '') {
      return this.normalizeDisplayValue(field, inputValue);
    }

    const initialValue = field.dropdownObj?.initialValue;
    if (initialValue !== undefined && initialValue !== null && initialValue !== '') {
      return initialValue;
    }

    return '';
  }

  private buildDisplayPatchValue(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    this.fields.forEach((field) => {
      if (this.isDateRangeField(field)) {
        const value = this.resolveControlValue(field) as { start: Date | null; end: Date | null };
        patch[`${field.key}_start`] = value.start;
        patch[`${field.key}_end`] = value.end;
      } else {
        patch[field.key] = this.resolveControlValue(field);
      }
    });
    return patch;
  }

  private normalizeDisplayValue(field: FilterField, value: unknown): unknown {
    if (field.type !== 'text') {
      return value;
    }

    const textValue = `${value ?? ''}`.trim();
    return textValue.replace(/^%+|%+$/g, '');
  }

  getFieldValue(field: FilterField): unknown {
    const value = this.form.controls[field.key]?.value;
    return value === '' ? null : value;
  }

  onTextInput(field: FilterField): void {
    if (!field.numericOnly) {
      return;
    }

    const control = this.form.controls[field.key];
    if (!control) {
      return;
    }

    const current = `${control.value ?? ''}`;
    const digitsOnly = current.replace(/\D+/g, '');
    if (current !== digitsOnly) {
      control.setValue(digitsOnly, { emitEvent: false });
    }
  }

  isDateRangeField(field: FilterField): boolean {
    return field.type === 'dateRange' || field.type === 'date-range';
  }

  onDropdownChange(field: FilterField, event: { value: unknown; option?: DropdownOptionLike }): void {
    const control = this.form.controls[field.key];
    if (!control) {
      return;
    }
    control.setValue(event.value ?? '', { emitEvent: false });
    this.updateDependentDropdowns(field, event.option);
    this.refreshAllRequestFilters();
  }

  onDropdownOptionsLoaded(field: FilterField, event: { name: string; options: DropdownOptionLike[] }): void {
    const dropdownConfig = this.getDropdownObj(field);
    if (event.name !== dropdownConfig.name) {
      return;
    }

    this.loadedOptionsMap[field.key] = Array.isArray(event.options) ? event.options : [];
    this.hydrateDependentsForParent(field);
  }

  private updateDependentDropdowns(parentField: FilterField, parentOption?: DropdownOptionLike): void {
    const dependentFields = this.fields.filter((field) =>
      field.type === 'select' && field.dependsOnKey === parentField.key
    );

    dependentFields.forEach((field) => {
      const dependentControl = this.form.controls[field.key];
      if (dependentControl) {
        dependentControl.setValue('', { emitEvent: false });
      }

      const optionsKey = field.dependsOnOptionsKey;
      if (!optionsKey) {
        return;
      }
      const rawList = Array.isArray(parentOption?.[optionsKey])
        ? (parentOption?.[optionsKey] as unknown[])
        : [];

      const mappedOptions = rawList
        .map((item) => `${item ?? ''}`.trim())
        .filter((item) => item.length > 0)
        .map((item) => ({ value: item, label: item }));

      const currentConfig = this.dropdownConfigMap[field.key] ?? this.createDropdownObj(field);
      this.dropdownConfigMap[field.key] = {
        ...currentConfig,
        options: mappedOptions,
        apiInfo: undefined
      };
    });
  }

  private hydrateDependentDropdowns(): void {
    this.refreshAllRequestFilters();
    const dependentFields = this.fields.filter((field) =>
      field.type === 'select' && !!field.dependsOnKey && !!field.dependsOnOptionsKey
    );

    dependentFields.forEach((dependentField) => {
      const parentField = this.fields.find((field) => field.key === dependentField.dependsOnKey);
      if (!parentField || parentField.type !== 'select') {
        return;
      }

      const parentValue = this.form.controls[parentField.key]?.value;
      if (parentValue === '' || parentValue === null || parentValue === undefined) {
        this.applyDependentOptions(dependentField, []);
        return;
      }

      const parentConfig = this.getDropdownObj(parentField);
      const loadedOptions = this.loadedOptionsMap[parentField.key];
      const sourceOptions = Array.isArray(loadedOptions) ? loadedOptions : [];
      if (sourceOptions.length === 0) {
        this.applyDependentOptions(dependentField, []);
        return;
      }

      this.applyDependentOptionsFromParentSelection(dependentField, parentConfig, sourceOptions, parentValue);
    });
  }

  private hydrateDependentsForParent(parentField: FilterField): void {
    const dependentFields = this.fields.filter((field) =>
      field.type === 'select' && field.dependsOnKey === parentField.key && !!field.dependsOnOptionsKey
    );

    if (dependentFields.length === 0) {
      return;
    }

    const parentControl = this.form.controls[parentField.key];
    const parentValue = parentControl?.value;
    if (parentValue === '' || parentValue === null || parentValue === undefined) {
      dependentFields.forEach((dependentField) => this.applyDependentOptions(dependentField, []));
      return;
    }

    const parentConfig = this.getDropdownObj(parentField);
    const parentSourceOptions = this.loadedOptionsMap[parentField.key] ?? [];
    dependentFields.forEach((dependentField) =>
      this.applyDependentOptionsFromParentSelection(dependentField, parentConfig, parentSourceOptions, parentValue)
    );
  }

  private applyDependentOptionsFromParentSelection(
    dependentField: FilterField,
    parentConfig: Partial<DropdownInfoConfig>,
    sourceOptions: DropdownOptionLike[],
    parentValue: unknown
  ): void {
    const parentValueKey = parentConfig.valueKey ?? 'value';
    const matchedParent = sourceOptions.find((item) =>
      `${item[parentValueKey] ?? ''}` === `${parentValue ?? ''}`
    );

    const optionsKey = dependentField.dependsOnOptionsKey!;
    const rawList = Array.isArray(matchedParent?.[optionsKey])
      ? (matchedParent?.[optionsKey] as unknown[])
      : [];

    const mappedOptions = rawList
      .map((item) => `${item ?? ''}`.trim())
      .filter((item) => item.length > 0)
      .map((item) => ({ value: item, label: item }));

    this.applyDependentOptions(dependentField, mappedOptions);
  }

  private applyDependentOptions(dependentField: FilterField, options: DropdownOptionLike[]): void {
    const currentConfig = this.dropdownConfigMap[dependentField.key] ?? this.createDropdownObj(dependentField);
    this.dropdownConfigMap[dependentField.key] = {
      ...currentConfig,
      options,
      apiInfo: undefined
    };

    const dependentControl = this.form.controls[dependentField.key];
    if (!dependentControl) {
      return;
    }

    const currentValue = dependentControl.value;
    const exists = options.some((item) => `${item['value'] ?? ''}` === `${currentValue ?? ''}`);
    if (!exists) {
      dependentControl.setValue('', { emitEvent: false });
    }
  }

  private createRequestFiltersForField(field: FilterField): Record<string, unknown> | null {
    if (field.type !== 'select' || !field.dependsOnKey || field.dependsOnOptionsKey) {
      return null;
    }

    const parentControl = this.form?.controls?.[field.dependsOnKey];
    const parentValue = parentControl?.value;
    if (parentValue === '' || parentValue === null || parentValue === undefined) {
      return null;
    }

    const filterKey = field.dependsOnFilterKey || field.dependsOnKey;
    return { [filterKey]: parentValue };
  }

  private refreshAllRequestFilters(): void {
    this.fields.forEach((field) => {
      if (field.type === 'select') {
        this.dropdownRequestFiltersMap[field.key] = this.createRequestFiltersForField(field);
      }
    });
  }

  private toDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const text = `${value}`.trim();
    if (!text) {
      return null;
    }
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toApiDate(value: unknown): string | null {
    const date = this.toDate(value);
    if (!date) {
      return null;
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
